import type { ModuleType } from '../../../types/course';
import type { Milestone } from '../../../types/gamification';

// ─── Step Types ──────────────────────────────────────────────────────────

export type WizardStepNumber = 1 | 2 | 3 | 4;
export type StepStatus = 'pending' | 'saved' | 'active';

export interface CourseDetailsData {
  title: string;
  subtitle?: string;
  description: string;
  imageUrl?: string;
  estimatedDurationMinutes: number;
}

export interface GamificationData {
  is_enabled: boolean;
  milestones: Milestone[];
}

export type CertificateChoice = 'none' | 'auto' | 'custom';

export interface CertificateData {
  choice: CertificateChoice;
  templateKey: string | null;
  issuerName: string;
  expirationMonths: number | null;
}

export interface WizardStepState {
  status: StepStatus;
}

export interface WizardState {
  courseId: string | null;
  steps: Record<WizardStepNumber, WizardStepState>;
}

// ─── Constants ───────────────────────────────────────────────────────────

export const STEP_LABELS: Record<WizardStepNumber, string> = {
  1: 'Basic',
  2: 'Content',
  3: 'Gamification (optional)',
  4: 'Certification (optional)',
};

export const STEP_DESCRIPTIONS: Record<WizardStepNumber, string> = {
  1: 'Set the title, description, and cover image for your course.',
  2: 'Build sections and add content modules to your course.',
  3: 'Configure gamification milestones and point rewards.',
  4: 'Choose how learners receive certificates upon completion.',
};

export const REMINDER_STORAGE_KEYS: Record<WizardStepNumber, string> = {
  1: 'wizard-reminder-step1-dismissed',
  2: 'wizard-reminder-step2-dismissed',
  3: 'wizard-reminder-step3-dismissed',
  4: 'wizard-reminder-step4-dismissed',
};

export const REMINDER_MESSAGES: Record<WizardStepNumber, { title: string; message: string }> = {
  1: {
    title: 'Getting Started',
    message: 'Fill in the course details below. A compelling title and cover image help learners discover your course.',
  },
  2: {
    title: 'Building Content',
    message: 'Please, drag and drop the content within the sections in the same order as you want the learners to access each content. Example: The first content in the first section will be accessed first and so on. Each content will be unlocked as the previous one is finished.',
  },
  3: {
    title: 'Gamification',
    message: 'You can choose various items for the same course. Note that learners that do not achieve any of the milestones set by you, will automatically receive 0 points from this course.',
  },
  4: {
    title: 'Certification',
    message: 'Upload a certificate template or use the default. Learners receive this upon course completion.',
  },
};

export const ALL_STEPS: WizardStepNumber[] = [1, 2, 3, 4];

// ─── Content Type Picker ─────────────────────────────────────────────────

export type ContentCategory = 'text' | 'upload_pdf' | 'upload_photo' | 'upload_video' | 'upload_scorm' | 'url' | 'quiz' | 'feedback';

export interface ContentTypeOption {
  category: ContentCategory;
  label: string;
  description: string;
  moduleType: ModuleType;
}

export const CONTENT_TYPE_OPTIONS: ContentTypeOption[] = [
  { category: 'text', label: 'Free text', description: 'Rich text content', moduleType: 'rich_text' },
  { category: 'upload_pdf', label: 'PDF', description: 'Upload a PDF file', moduleType: 'pdf_file' },
  { category: 'upload_photo', label: 'Photo / GIF', description: 'Upload an image', moduleType: 'image_embed' },
  { category: 'upload_video', label: 'Video', description: 'Upload a video', moduleType: 'video_url_embed' },
  { category: 'upload_scorm', label: 'SCORM', description: 'Upload a SCORM package', moduleType: 'scorm_zip' },
  { category: 'url', label: 'URL', description: 'Add an external URL', moduleType: 'video_meeting_link' },
  { category: 'quiz', label: 'Quiz', description: 'Create a quiz', moduleType: 'evaluation' },
  { category: 'feedback', label: 'Survey', description: 'Collect learner feedback', moduleType: 'feedback' },
];

// ─── Modal State (Step 2) ────────────────────────────────────────────────

export type Step2ModalState =
  | { kind: 'none' }
  | { kind: 'addSection' }
  | { kind: 'editSection'; sectionId: string; title: string; imageUrl: string | null }
  | { kind: 'pickContentType'; sectionId: string }
  | { kind: 'contentTitle'; sectionId: string; contentType: ContentTypeOption }
  | { kind: 'editor'; sectionId: string; moduleId: string; moduleType: ModuleType };
